import type { Config } from "tailwindcss";
import plugin from "tailwindcss/plugin";
import tailwindcssAnimate from "tailwindcss-animate";
import typography from "@tailwindcss/typography";
import containerQueries from "@tailwindcss/container-queries";

export default {
  darkMode: ["class"],
  theme: {
    extend: {
      colors: {
        brand: {
          50: "#fef5e7",
          100: "#FCE6BF",
          200: "#FBD99D",
          300: "#F9C771",
          400: "#F7B440",
          500: "#F59E0B",
          600: "#DC8F09",
          700: "#BF7C08",
          800: "#A26907",
          900: "#764C05",
          950: "#4E3303",
          DEFAULT: "#F59E0B",
          foreground: "#000000",
        },
        // info: tenant-branding default for the "info" semantic role
        // (consumed by apps/admin/.../branding/utils.ts). Distinct from
        // `blue` (Sunfish substrate) so tenants get a brighter info-blue
        // rather than the deep navy.
        info: {
          50: "#EAF3FB",
          100: "#CDE3F6",
          200: "#A2CAEE",
          300: "#6FACDD",
          400: "#3F8DD0",
          500: "#1D7ED8",
          600: "#1968B0",
          700: "#155389",
          800: "#103E66",
          900: "#0A2C4C",
          DEFAULT: "#1D7ED8",
          foreground: "#FFFFFF",
        },
        // blue: the Sunfish identity navy. The 500 step is the canonical deep
        // navy used on the sidebar and default-variant button. Below 500 the
        // scale lightens through info/sky territory; above 500 it darkens
        // toward a near-black navy (never pure black, per DESIGN.md's
        // No-Pure-Color Rule).
        blue: {
          50: "#DBEBFA",
          100: "#C4DFF7",
          200: "#9CC8F2",
          300: "#61A8EA",
          400: "#1D7ED8",
          500: "#0A2C4C",
          600: "#091F38",
          700: "#071829",
          800: "#05121E",
          900: "#040C15",
          950: "#02080F",
          DEFAULT: "#0A2C4C",
          foreground: "#FFFFFF",
        },
        green: {
          50: "#f0fdf4",
          100: "#dcfce7",
          200: "#bbf7d0",
          300: "#86efac",
          400: "#4ade80",
          500: "#22c55e",
          600: "#16a34a",
          700: "#15803d",
          800: "#166534",
          900: "#14532d",
          950: "#052e16",
          DEFAULT: "#16a34a",
          foreground: "#FFFFFF",
        },
        gray: {
          DEFAULT: "#4a4f55",
          50: "#f9fafb",
          100: "#f3f4f6",
          200: "#e5e7eb",
          300: "#d1d5dc",
          400: "#5a5f7d",
          500: "#4a4f5f",
          600: "#41434b",
          700: "#374151",
          800: "#1f2937",
          900: "#111827",
          950: "#0d0e11",
          foreground: "#FFFFFF",
        },
        background: "hsl(var(--background))",
        foreground: "hsl(var(--foreground))",
        transparent: "transparent",
        card: {
          DEFAULT: "hsl(var(--card))",
          foreground: "hsl(var(--card-foreground))",
        },
        "card-alternate": {
          DEFAULT: "hsl(var(--card-alternate))",
          foreground: "hsl(var(--card-alternate-foreground))",
        },
        popover: {
          DEFAULT: "hsl(var(--popover))",
          foreground: "hsl(var(--popover-foreground))",
        },
        primary: {
          DEFAULT: "hsl(var(--primary))",
          foreground: "hsl(var(--primary-foreground))",
        },
        secondary: {
          DEFAULT: "hsl(var(--secondary))",
          foreground: "hsl(var(--secondary-foreground))",
        },
        muted: {
          DEFAULT: "hsl(var(--muted))",
          foreground: "hsl(var(--muted-foreground))",
        },
        accent: {
          DEFAULT: "hsl(var(--accent))",
          foreground: "hsl(var(--accent-foreground))",
        },
        destructive: {
          DEFAULT: "hsl(var(--destructive))",
          foreground: "hsl(var(--destructive-foreground))",
        },
        positive: {
          DEFAULT: "hsl(var(--positive))",
          foreground: "hsl(var(--positive-foreground))",
        },
        warning: {
          DEFAULT: "hsl(var(--warning))",
          foreground: "hsl(var(--warning-foreground))",
        },
        border: "hsl(var(--border))",
        input: "hsl(var(--input))",
        ring: "hsl(var(--ring))",
        chart: {
          "1": "hsl(var(--chart-1))",
          "2": "hsl(var(--chart-2))",
          "3": "hsl(var(--chart-3))",
          "4": "hsl(var(--chart-4))",
          "5": "hsl(var(--chart-5))",
        },
        sidebar: {
          DEFAULT: "hsl(var(--sidebar-background))",
          foreground: "hsl(var(--sidebar-foreground))",
          primary: "hsl(var(--sidebar-primary))",
          "primary-foreground": "hsl(var(--sidebar-primary-foreground))",
          accent: "hsl(var(--sidebar-accent))",
          "accent-foreground": "hsl(var(--sidebar-accent-foreground))",
          border: "hsl(var(--sidebar-border))",
          ring: "hsl(var(--sidebar-ring))",
        },
      },
      borderRadius: {
        lg: "var(--radius)",
        md: "calc(var(--radius) - 2px)",
        sm: "calc(var(--radius) - 4px)",
      },
      // DESIGN.md typography scale. Use these aliases (text-display,
      // text-headline, text-title, text-body, text-label) instead of raw
      // text-2xl / text-sm so the named hierarchy stays in code.
      fontSize: {
        display: ["2.25rem", { lineHeight: "1.1", letterSpacing: "-0.02em", fontWeight: "600" }],
        headline: ["1.5rem", { lineHeight: "1.2", letterSpacing: "-0.01em", fontWeight: "600" }],
        title: ["1.25rem", { lineHeight: "1.2", letterSpacing: "-0.01em", fontWeight: "600" }],
        body: ["0.875rem", { lineHeight: "1.5", fontWeight: "400" }],
        label: ["0.75rem", { lineHeight: "1.4", fontWeight: "500" }],
      },
      // DESIGN.md spacing scale aliases. Tailwind's default p-1/p-2/p-4/p-6/p-8
      // already cover these values; the aliases just let code reference the
      // DESIGN.md token names directly (p-xs, gap-md, etc.).
      spacing: {
        xs: "0.25rem",
        sm: "0.5rem",
        md: "1rem",
        lg: "1.5rem",
        xl: "2rem",
      },
      maxWidth: {
        page: "1200px",
      },
    },
  },
  plugins: [
    tailwindcssAnimate,
    typography,
    containerQueries,
    plugin(({ addVariant }) => {
      addVariant("not-last", "&:not(:last-child)");
    }),
  ],
  content: [],
} satisfies Config;
